import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
    getChallengeDetails,
    joinChallenge,
    leaveChallenge,
    endChallenge,
    restartChallenge,
    promoteParticipant,
    getRecurrenceHistory,
    getInvitableFriends,
    inviteToChallenge,
} from '../api/challenges';
import { getUserId } from '../storage';
import {
    CHALLENGE_TYPE_DESC,
    STATUS_LABELS,
    challengeStatItems,
    formatSp,
    splitChallengeDate,
} from '../constants/challenges';
import ChallengeStatGrid from '../components/ChallengeStatGrid';
import useToast from '../hooks/useToast';
import '../styles/ChallengesPage.css';

const StatusBadge = ({ status }) => (
    <span className={`ch-badge ch-badge--${status}`}>
        {STATUS_LABELS[status] || status}
    </span>
);

const UserButton = ({ username, className, children }) => {
    const history = useHistory();
    if (!username) return <span className={className}>{children}</span>;
    return (
        <button
            type="button"
            className={`ch-clickable ${className || ''}`}
            onClick={() => history.push(`/user/${username}`)}
        >
            {children}
        </button>
    );
};

const weekStats = (week) => {
    const start = splitChallengeDate(week.start);
    const end = splitChallengeDate(week.end);
    return [
        { label: 'Total', value: formatSp(week.total_sp), unit: 'SP', tone: 'points' },
        { label: 'Players', value: String((week.leaderboard || []).length) },
        { label: 'Starts', value: start.value, unit: start.unit },
        { label: 'Ends', value: end.value, unit: end.unit },
    ];
};

const ChallengeDetailPage = () => {
    const { challengeId } = useParams();
    const history = useHistory();

    const [challenge, setChallenge] = useState(null);
    const [recurrenceHistory, setRecurrenceHistory] = useState([]);
    const [standingsError, setStandingsError] = useState('');
    const [loading, setLoading] = useState(true);
    const { error, successMessage, showSuccess, showError } = useToast();
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [invitableFriends, setInvitableFriends] = useState([]);
    const [inviteLoading, setInviteLoading] = useState(false);

    const fetchDetails = useCallback(async () => {
        try {
            const data = await getChallengeDetails(challengeId);
            setChallenge(data);
            if (data.user_role) {
                try {
                    const hist = await getRecurrenceHistory(challengeId);
                    setRecurrenceHistory(Array.isArray(hist) ? hist : []);
                    setStandingsError('');
                } catch (err) {
                    setRecurrenceHistory([]);
                    setStandingsError(err.message || 'Failed to load standings.');
                }
            } else {
                setRecurrenceHistory([]);
                setStandingsError('');
            }
        } catch (err) {
            showError(err.message || 'Failed to load challenge details.');
        } finally {
            setLoading(false);
        }
    }, [challengeId]);

    useEffect(() => {
        setLoading(true);
        fetchDetails();
    }, [fetchDetails]);

    const handleJoin = async () => {
        try {
            await joinChallenge(challengeId);
            showSuccess('Successfully joined the challenge!');
            await fetchDetails();
        } catch (err) {
            showError(err.message || 'Failed to join challenge.');
        }
    };

    const handleLeave = async () => {
        if (!window.confirm('Are you sure you want to leave this challenge?')) return;
        try {
            await leaveChallenge(challengeId);
            showSuccess('Successfully left the challenge.');
            history.push('/challenges');
        } catch (err) {
            showError(err.message || 'Failed to leave challenge.');
        }
    };

    const handleEnd = async () => {
        if (!window.confirm('Are you sure you want to end this challenge?')) return;
        try {
            await endChallenge(challengeId);
            showSuccess('Challenge ended.');
            await fetchDetails();
        } catch (err) {
            showError(err.message || 'Failed to end challenge.');
        }
    };

    const handleRestart = async () => {
        if (!window.confirm('Restart this challenge? The start time will be reset to now.')) return;
        try {
            await restartChallenge(challengeId);
            showSuccess('Challenge restarted!');
            await fetchDetails();
        } catch (err) {
            showError(err.message || 'Failed to restart challenge.');
        }
    };

    const handlePromote = async (userId, username) => {
        if (!window.confirm(`Promote ${username || userId} to admin?`)) return;
        try {
            await promoteParticipant(challengeId, userId);
            showSuccess(`${username || 'User'} promoted to admin!`);
            await fetchDetails();
        } catch (err) {
            showError(err.message || 'Failed to promote participant.');
        }
    };

    const handleOpenInviteModal = async () => {
        try {
            setInviteLoading(true);
            const friends = await getInvitableFriends(challengeId);
            setInvitableFriends(Array.isArray(friends) ? friends : []);
            setShowInviteModal(true);
        } catch (err) {
            showError(err.message || 'Failed to load friends.');
        } finally {
            setInviteLoading(false);
        }
    };

    const handleInvite = async (friendId, username) => {
        try {
            await inviteToChallenge(challengeId, friendId);
            showSuccess(`Invite sent to ${username || 'user'}!`);
            setInvitableFriends((prev) => prev.map((friend) => (
                friend.auth_id === friendId ? { ...friend, invite_status: 'pending' } : friend
            )));
        } catch (err) {
            showError(err.message || 'Failed to send invite.');
        }
    };

    if (loading) {
        return (
            <div className="sc-page">
                <div className="sc-container">
                    <div className="sc-loading">
                        <div className="sc-spinner"></div>
                        <p>Loading challenge...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!challenge) {
        return (
            <div className="sc-page">
                <div className="sc-container ch-shell">
                    <div className="ch-back-bar">
                        <button type="button" className="sc-back-link" onClick={() => history.push('/challenges')}>
                            &larr; Challenges
                        </button>
                    </div>
                    {error && (
                        <div className="ch-alerts">
                            <div className="sc-alert sc-alert--error">{error}</div>
                        </div>
                    )}
                    <p className="ch-empty">Challenge not found.</p>
                </div>
            </div>
        );
    }

    const c = challenge;
    const isAdmin = c.user_role === 'admin';
    const isMember = c.user_role === 'member';
    const isParticipant = isAdmin || isMember;
    const isActive = c.computed_status === 'active';
    const isRecurring = (c.max_recurrences || 0) > 0;
    const currentWeek = recurrenceHistory.find((week) => week.is_current);
    const completedWeeks = recurrenceHistory.filter((week) => week.is_completed);
    const participantById = Object.fromEntries((c.participants || []).map((p) => [p.user_id, p]));
    const userId = getUserId();
    const stats = challengeStatItems(c, { detailed: true });

    if (isParticipant && isActive && c.challenge_type === 'coop' && currentWeek && Number(c.sp_goal) > 0) {
        const goalStat = stats.find((stat) => stat.label === 'Goal');
        if (goalStat) {
            goalStat.meter = Math.min(
                100,
                Math.round(((Number(currentWeek.total_sp) || 0) / Number(c.sp_goal)) * 100),
            );
        }
    }

    const renderLeaderboard = (leaderboard) => (
        <div className="ch-leaderboard">
            {leaderboard.map((entry, idx) => {
                const participant = participantById[entry.user_id];
                const canPromote = isAdmin && participant?.role === 'member';
                return (
                    <div
                        key={entry.user_id}
                        className={`ch-lb-row${entry.user_id === userId ? ' is-me' : ''}`}
                    >
                        <span className="ch-lb-rank">#{idx + 1}</span>
                        <div className="ch-lb-body">
                            <div className="ch-lb-topline">
                                <UserButton username={entry.username} className="ch-lb-name">
                                    {entry.username || entry.user_id}
                                </UserButton>
                                <span className="ch-lb-sp">{formatSp(entry.sp_earned)} SP</span>
                            </div>
                            {participant && (
                                <div className="ch-lb-actions">
                                    <span className={`ch-role ch-role--${participant.role}`}>
                                        {participant.role === 'admin' ? 'Admin' : 'Member'}
                                    </span>
                                    {canPromote && (
                                        <button
                                            type="button"
                                            className="btn-promote"
                                            onClick={() => handlePromote(entry.user_id, entry.username)}
                                        >
                                            Promote
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="sc-page">
            <div className="sc-container ch-shell">
                <div className="ch-back-bar">
                    <button type="button" className="sc-back-link" onClick={() => history.push('/challenges')}>
                        &larr; Challenges
                    </button>
                </div>

                <header className="sc-header ch-header">
                    <div className="ch-header-icon" aria-hidden="true">{c.icon || '🏆'}</div>
                    <div className="ch-header-text">
                        <h2>{c.name}</h2>
                        <div className="ch-badges">
                            <StatusBadge status={c.computed_status} />
                            {c.user_role && (
                                <span className={`ch-role ch-role--${c.user_role}`}>
                                    {c.user_role === 'admin' ? 'Admin' : 'Member'}
                                </span>
                            )}
                        </div>
                        <p className="ch-header-sub">
                            {CHALLENGE_TYPE_DESC[c.challenge_type] || c.challenge_type}
                        </p>
                    </div>
                </header>

                {(error || successMessage) && (
                    <div className="ch-alerts">
                        {error && <div className="sc-alert sc-alert--error">{error}</div>}
                        {successMessage && <div className="sc-alert sc-alert--success">{successMessage}</div>}
                    </div>
                )}

                <ChallengeStatGrid stats={stats} label={`${c.name} summary`} />

                <div className="ch-content">
                    {isParticipant && isActive && (
                        <section className="ch-section">
                            <div>
                                <h3 className="ch-section-title">Live standings</h3>
                                <p className="ch-note">
                                    {isRecurring
                                        ? 'SpudPoints earned so far this week'
                                        : 'SpudPoints earned so far'}
                                </p>
                            </div>
                            {standingsError && <div className="sc-alert sc-alert--error">{standingsError}</div>}
                            {currentWeek ? (
                                renderLeaderboard(currentWeek.leaderboard)
                            ) : !standingsError ? (
                                <p className="ch-empty">No standings yet. Log an activity to start earning SP.</p>
                            ) : null}
                        </section>
                    )}

                    {isParticipant && !isActive && c.participants && (
                        <section className="ch-section">
                            <h3 className="ch-section-title">Participants</h3>
                            <div className="ch-person-list">
                                {c.participants.map((person) => (
                                    <div key={person.user_id} className="ch-person">
                                        <UserButton username={person.username} className="ch-person-name">
                                            {person.username || person.user_id}
                                        </UserButton>
                                        <span className={`ch-role ch-role--${person.role}`}>
                                            {person.role === 'admin' ? 'Admin' : 'Member'}
                                        </span>
                                        {isAdmin && person.role === 'member' && (
                                            <button
                                                type="button"
                                                className="btn-promote"
                                                onClick={() => handlePromote(person.user_id, person.username)}
                                            >
                                                Promote
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {isParticipant && completedWeeks.length > 0 && (
                        <section className="ch-section">
                            <h3 className="ch-section-title">
                                {isRecurring ? 'Past weeks' : 'Results'}
                            </h3>
                            {completedWeeks.map((week) => (
                                <article key={week.week} className="ch-week">
                                    <div className="ch-week-top">
                                        <span className="ch-week-title">
                                            {isRecurring ? `Week ${week.week}` : 'Challenge period'}
                                        </span>
                                        <span className="ch-badge ch-badge--completed">Completed</span>
                                    </div>
                                    <ChallengeStatGrid
                                        stats={weekStats(week)}
                                        label={isRecurring ? `Week ${week.week} summary` : 'Results summary'}
                                    />
                                    {week.winners.length > 0 && (
                                        <p className="ch-winners">
                                            <span aria-hidden="true">🏆</span>
                                            {week.winners.map((name, index) => (
                                                <span key={name}>
                                                    <UserButton username={name}>{name}</UserButton>
                                                    {index < week.winners.length - 1 ? ', ' : ''}
                                                </span>
                                            ))}
                                        </p>
                                    )}
                                    {renderLeaderboard(week.leaderboard)}
                                </article>
                            ))}
                        </section>
                    )}
                </div>

                <div className="ch-detail-actions">
                    {!isParticipant && c.user_role === null && (
                        <button type="button" className="sc-btn sc-btn--primary" onClick={handleJoin}>
                            Join Challenge
                        </button>
                    )}
                    {isParticipant && (
                        <button
                            type="button"
                            className="sc-btn sc-btn--primary"
                            onClick={handleOpenInviteModal}
                            disabled={inviteLoading}
                        >
                            {inviteLoading ? 'Loading...' : 'Invite Friends'}
                        </button>
                    )}
                    {isAdmin && (
                        <button
                            type="button"
                            className="sc-btn sc-btn--secondary"
                            onClick={() => history.push('/challenges', { editChallengeId: c.challenge_id })}
                        >
                            Edit
                        </button>
                    )}
                    {isAdmin && isActive && (
                        <button type="button" className="sc-btn sc-btn--danger" onClick={handleEnd}>
                            End Challenge
                        </button>
                    )}
                    {isAdmin && (c.computed_status === 'completed' || c.computed_status === 'ended') && (
                        <button type="button" className="sc-btn sc-btn--primary" onClick={handleRestart}>
                            Restart
                        </button>
                    )}
                    {isParticipant && (
                        <button type="button" className="sc-btn sc-btn--danger" onClick={handleLeave}>
                            Leave
                        </button>
                    )}
                </div>

                {showInviteModal && (
                    <div className="sc-overlay ch-overlay" onClick={() => setShowInviteModal(false)}>
                        <div
                            className="sc-modal ch-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="ch-invite-title"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="ch-modal-head">
                                <h2 id="ch-invite-title">Invite friends</h2>
                                <button
                                    type="button"
                                    className="ch-modal-close"
                                    onClick={() => setShowInviteModal(false)}
                                    aria-label="Close"
                                >
                                    &times;
                                </button>
                            </div>
                            {invitableFriends.length === 0 ? (
                                <p className="ch-empty">
                                    No friends available to invite. They may already be in this challenge or have a pending invite.
                                </p>
                            ) : (
                                <div className="ch-person-list">
                                    {invitableFriends.map((friend) => (
                                        <div key={friend.auth_id} className="ch-person">
                                            <span className="ch-person-name">{friend.username || friend.auth_id}</span>
                                            {friend.invite_status === 'pending' ? (
                                                <span className="ch-person-status">Pending</span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="sc-btn sc-btn--primary sc-btn--sm"
                                                    onClick={() => handleInvite(friend.auth_id, friend.username)}
                                                >
                                                    Invite
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChallengeDetailPage;
